import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';

import Swal from 'sweetalert2';

import { Box, MenuItem, Paper, MenuList, Divider, Switch } from '@mui/material';
import { BarcodeInput, ItemPaper } from '../../../../../components';
import { SCUIButton } from '../../../../../../atomic/atoms';

import { PlanningQuery } from '../../../../../../services/studio/StudioQuery';
import { handleStudioApiResponse } from '../../../../../helper/studio_helper';
import { PlanningCommand } from '../../../../../../services/studio/StudioCommand';

import AddIcon from '@mui/icons-material/Add';

export const ItemListTab = ({ setIsLoading, planningDetail, setActiveStep }: any) => {
    const [barcode, setBarcode] = useState<string>('');
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [barcodeList, setBarcodeList] = useState<any[]>([]);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const { t } = useTranslation();
    const { planningId } = useParams<any>();
    const history = useHistory();

    const planId = planningId || planningDetail?.id; //Put veya create sayfa kontrolü

    const barcodeInputRef = useRef<HTMLInputElement>(null);

    const handleSave = (code?: any) => {
        setIsLoading(true);
        if (code) {
            handleStudioApiResponse(
                PlanningCommand.PostPlanningShot({
                    planingId: planId,
                    barcode: code,
                }),
                () => {
                    setBarcode('');

                    if (barcodeInputRef.current) {
                        barcodeInputRef.current.value = '';
                    }

                    Swal.fire({
                        icon: 'success',
                        title: t('studio.planning.shotAdded'),
                        showConfirmButton: false,
                        timer: 500,
                    });
                },
                () => getShots(),
                t
            );
        }
    };

    const handleDeleteBarcode = (id: any) => {
        setIsLoading(true);
        handleStudioApiResponse(
            PlanningCommand.DeletePlanningShot({
                planingId: planId,
                shotId: id,
            }),
            () => {
                Swal.fire({
                    icon: 'success',
                    title: t('studio.planning.shotDeleted'),
                    showConfirmButton: false,
                    timer: 1500,
                });
            },
            () => getShots(),
            t
        );
    };

    const handleItemDetail = (item: any) => {
        setSelectedItem({ ...item, productAcceptanceDate: item.date });
    };

    const getShots = () => {
        setIsLoading(true);
        handleStudioApiResponse(
            PlanningQuery.GetPlanningShots({
                PlaningId: planId,
            }),
            (res: any) => {
                setBarcodeList(res.data?.items?.reverse() || []);
                if (res.data?.items?.length > 0) {
                    setSelectedItem({ ...res.data?.items?.[0], productAcceptanceDate: res.data?.items?.[0]?.date });
                } else {
                    setSelectedItem(null);
                }
            },
            () => setIsLoading(false),
            t
        );
    };

    const handleCreateNewPlanning = () => {
        history.push('/studio/planning-create');
        setActiveStep(0);
    };

    useEffect(() => {
        getShots();
    }, []);

    return (
        <Box>
            <Box
                className="studio-form-paper row"
                sx={{ minWidth: '100%', display: 'flex', alignItems: 'center !important' }}
            >
                <Box
                    className="form-input-container"
                    sx={{
                        '& .Mui-checked': {
                            color: '#B1020B !important',
                        },
                        '& .MuiSwitch-track': {
                            backgroundColor: '#B1020B !important',
                        },
                        '& .studio-form-label': {
                            color: '#B1020B !important',
                            marginRight: '0px !important',
                        },
                    }}
                >
                    <p className="studio-form-label side">{t('studio.planning.deleteWithBarcode')}:</p>
                    <Switch
                        checked={isDeleting}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setIsDeleting(event.target.checked);
                            setTimeout(() => barcodeInputRef.current?.focus(), 0);
                        }}
                    />
                </Box>
                <BarcodeInput
                    value={barcode}
                    onChange={setBarcode}
                    onValidBarcodeEnter={(code) => {
                        if (isDeleting) {
                            const idToDelete = barcodeList?.find((item: any) => item.barcode === +code)?.id;
                            handleDeleteBarcode(idToDelete);
                            return;
                        }
                        handleSave(code);
                    }}
                    inputRef={barcodeInputRef}
                    autoFocus
                />
                <Box className="form-input-container">
                    <p style={{ marginLeft: '16px' }} className="studio-form-label">
                        {t('studio.planning.totalBarcodeAmount')}: {barcodeList?.length || 0}
                    </p>
                </Box>
                <Box className="form-input-container">
                    <SCUIButton
                        text={t('studio.planning.createPlanning')}
                        type="primary"
                        sizeType="fixed"
                        style={{ height: '35px' }}
                        size={'m'}
                        buttonType="button"
                        onClick={handleCreateNewPlanning}
                        showStartIcon
                        startIcon={<AddIcon sx={{ color: '#fff' }} />}
                    />
                </Box>
            </Box>
            <Box sx={{ marginTop: '16px', display: 'flex', flexDirection: 'row' }}>
                <Box
                    className="studio-form-paper"
                    sx={{
                        overflow: 'auto',
                        height: '300px',
                        maxWidth: '300px',
                        marginRight: '24px',
                        padding: '8px 24px !important',
                        justifyContent: 'center !important',
                    }}
                >
                    {barcodeList?.length !== 0 ? (
                        <>
                            <Paper sx={{ width: '100%' }}>
                                <MenuList dense>
                                    {(barcodeList || []).map((item: any, index: any) => (
                                        <>
                                            <MenuItem
                                                sx={{
                                                    justifyContent: 'center',
                                                    paddingTop: '8px',
                                                    paddingBottom: '8px',
                                                }}
                                                selected={item.barcode === selectedItem?.barcode}
                                                onClick={() => handleItemDetail(item)}
                                            >
                                                <p className="studio-form-text">{item.barcode}</p>
                                            </MenuItem>
                                            {index + 1 !== barcodeList?.length ? (
                                                <Divider sx={{ margin: '0 !important' }} />
                                            ) : (
                                                <></>
                                            )}
                                        </>
                                    ))}
                                </MenuList>
                            </Paper>
                        </>
                    ) : (
                        <Box
                            sx={{
                                margin: '4px 0',
                                width: '100% !important',
                                display: 'flex',
                                alignItems: 'center !important',
                                justifyContent: 'center !important',
                            }}
                        >
                            <p className="studio-form-text">{t('studio.planning.scanBarcode')}</p>
                        </Box>
                    )}
                </Box>
                {selectedItem?.barcode ? (
                    <ItemPaper fullWidth={false} item={selectedItem} />
                ) : (
                    <Box
                        className="studio-form-paper"
                        sx={{
                            margin: '4px 0',
                            width: '400px !important',
                            display: 'flex',
                            alignItems: 'center !important',
                            justifyContent: 'center !important',
                            maxWidth: '50% !important',
                        }}
                    >
                        <p className="studio-form-text"> {t('studio.planning.noItemDetail')}</p>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
